/* eslint-disable @next/next/no-img-element */
"use client";

import { renderClassName } from "@/utils/renderClassName";
import { useEffect, useState } from "react";
import { Swiper as SwiperCore } from "swiper";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import ArrowNext from "/public/assets/images/media-library/ArrowNext.svg";
import ArrowPrev from "/public/assets/images/media-library/ArrowPrev.svg";
import Image from "next/image";
import { secondsToHMS } from "@/utils/convertTime";
import { motion } from "framer-motion";
import Button from "@/components/Button";
import dynamic from "next/dynamic";
import { NewsType } from "@/types/News";
import { useTranslations } from "next-intl";
import CloseIcon from "@/public/assets/images/X.svg";
import { Link } from "@/navigation";
import { useGetPlans } from "@/services/plans/useGetPlans";
import { PlanTypeValueType } from "@/types/Plans";
import { ProductType } from "@/types/Products";
import { ArrowRightOutlined } from "@ant-design/icons";
import {
  isProductComingSoon,
  isProductNew,
} from "@/utils/groupProductsByCategory";
import dayjs from "dayjs";
const DynamicModal = dynamic(() => import("@/components/Modal"), {
  ssr: false,
});

type ContentType = {
  title: string;
  cards?: ProductType[];
  perSlides?: number;
  categoryId?: number;
  link?: string;
};

type SliderCardsProps = {
  section: {
    type?: string;
    content: ContentType;
  };
  isNew?: boolean;
  isComingSoon?: boolean;
};

const SliderCards = ({ section, isComingSoon, isNew }: SliderCardsProps) => {
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [showPrev, setShowPrev] = useState(false);
  const [showNext, setShowNext] = useState(true);

  const handleSlideChange = (swiper: SwiperCore) => {
    setShowPrev(!swiper.isBeginning);
    setShowNext(!swiper.isEnd);
  };

  useEffect(() => {
    const checkTouchDevice = () => {
      if ("ontouchstart" in window || navigator.maxTouchPoints > 0) {
        setIsTouchDevice(true);
        document.documentElement.classList.add("touch");
      } else {
        setIsTouchDevice(false);
        document.documentElement.classList.remove("touch");
      }
    };

    checkTouchDevice();
  }, []);

  return (
    <section className="slider-cards">
      <div className="slider-cards__holder">
        <div className="slider-cards__title-holder">
          <h2 className="slider-cards__title">{section.content.title}</h2>
          {section.content.categoryId || section.content.link ? (
            <Link
              className="slider-cards__link"
              href={
                section.content.categoryId
                  ? `/media-library/category/${section.content.categoryId}`
                  : section.content.link
                  ? section.content.link
                  : ""
              }
            >
              открыть все ({section.content.cards?.length}){" "}
              <ArrowRightOutlined />
            </Link>
          ) : null}
        </div>
        <Swiper
          spaceBetween={14}
          slidesPerView={1}
          allowTouchMove={isTouchDevice}
          breakpoints={{
            320: {
              slidesPerView: 1,
            },
            780: {
              slidesPerView: 2,
            },
            1050: {
              slidesPerView: 3,
            },
            1350: {
              slidesPerView: 4,
            },
            1500: {
              slidesPerView: section.content.perSlides ? 5 : 4,
            },
            1700: {
              slidesPerView: section.content.perSlides ? 6 : 5,
            },
            1920: {
              slidesPerView: section.content.perSlides ? 7 : 6,
            },
          }}
          observer={true}
          onSlideChange={(swiper: SwiperCore) => handleSlideChange(swiper)}
          onInit={(swiper: SwiperCore) => handleSlideChange(swiper)}
        >
          {!isTouchDevice ? (
            <SliderControlButton
              isNotBanner
              showPrev={showPrev}
              showNext={showNext}
            />
          ) : null}
          {section.content.cards?.map((card, index) => (
            <SwiperSlide key={`${card.title}-${index}`}>
              <Card
                card={card}
                isComingSoon={
                  isComingSoon || card.activation_date
                    ? isProductComingSoon(card.activation_date!)
                    : false
                }
                isFree={card.free}
                isNew={
                  isNew || card.activation_date
                    ? isProductNew(card.activation_date)
                    : false
                }
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
};

type SliderControlButtonProps = {
  isNotBanner?: boolean;
  showNext: boolean;
  showPrev: boolean;
};

export const SliderControlButton = ({
  isNotBanner,
  showNext,
  showPrev,
}: SliderControlButtonProps) => {
  const swiper = useSwiper();
  return (
    <>
      <button
        className={renderClassName(
          "slider-button slider-button--prev",
          `${isNotBanner ? "slider-button--shifted" : ""}`
        )}
        onClick={() => {
          swiper.slidePrev();
        }}
        disabled={!showPrev}
      >
        <ArrowPrev />
      </button>

      <button
        className={renderClassName(
          "slider-button slider-button--next",
          `${isNotBanner ? "slider-button--shifted" : ""}`
        )}
        onClick={() => {
          swiper.slideNext();
        }}
        disabled={!showNext}
      >
        <ArrowNext />
      </button>
    </>
  );
};

export enum CardTags {
  DEFAULT = "DEFAULT",
  NEW = "NEW",
  PROMO = "PROMO",
  PREMIUM = "PREMIUM",
  LOCKED = "LOCKED",
  SUBSCRIPTION = "SUBSCRIPTION",
}

export type CardProps = {
  image: string;
  title: string;
  longTitle: string;
  description: string;
  author: string;
  viewed: number;
  duration?: number;
  tag?: CardTags;
  type: "video";
  isSmall?: boolean;
  video?: string;
  streamId?: string;
  isFree?: boolean;
};

export const Card = ({
  card,
  isSmall,
  isComingSoon,
  isNew,
  isFree,
}: {
  card: ProductType;
  isSmall?: boolean;
  isComingSoon?: boolean;
  isNew?: boolean;
  isFree?: boolean;
}) => {
  if (!isSmall) {
    return (
      <div className={`slider-card slider-card--opacity`.trim()}>
        <div>
          <div
            className="slider-card__top"
            style={{
              backgroundImage: `url(${card?.images?.primary_image_url})`,
            }}
          >
            {isComingSoon ? (
              <div className="slider-card__tag--date">
                <p>
                  {dayjs(card.activation_date)
                    .tz("Europe/Moscow")
                    .format("DD MM YYYY")}
                </p>
              </div>
            ) : null}
            <div className="slider-card__tag">
              {isComingSoon ? (
                <p className="slider-card__tag--soon">Скоро</p>
              ) : null}
              {isNew ? <p className="slider-card__tag--new">Новое</p> : null}
              {!isFree ? (
                <Image
                  src="/assets/images/Lock.png"
                  alt="Locked"
                  width={24}
                  height={24}
                />
              ) : null}
            </div>
            <div className="slider-card__content">
              <h3 className="slider-card__title">{card?.title}</h3>
            </div>
          </div>
          <CardHoverDetails card={card} />
          <div className="slider-card__bottom">
            {card.author ? (
              <p className="slider-card__author">
                <span>{card?.author}</span>
              </p>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`slider-card slider-card--small slider-card--opacity`.trim()}
    >
      <div>
        <div
          className="slider-card__top"
          style={{ backgroundImage: `url(${card?.images?.primary_image_url})` }}
        >
          <div className="slider-card__tag">
            <Image
              src="/assets/images/Lock.png"
              alt="Locked"
              width={24}
              height={24}
            />
          </div>
          <div className="slider-card__content">
            <h3 className="slider-card__title">{card?.title}</h3>
          </div>
        </div>
        <CardHoverDetails card={card} />
        <div className="slider-card__bottom">
          <p className="slider-card__author">
            <span>{card?.author}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

type CardHoveredDetailsProps = {
  card: ProductType;
};

const CardHoverDetails = ({ card }: CardHoveredDetailsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [type, setType] = useState<
    "Details" | "Video" | "Subscription" | undefined
  >();

  return (
    <div className="slider-card__hover-details">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.4 }}
      >
        <div className="slider-card__button-groups">
          {card.free ? (
            <button
              className="slider-card__play-button"
              onClick={() => {
                setIsOpen(true);
                setType("Video");
              }}
            >
              <img src="/assets/images/play.svg" alt="" role="presentation" />
            </button>
          ) : (
            <Button
              onClick={() => {
                setIsOpen(true);
                setType("Subscription");
              }}
              type="primary"
            >
              Подписка
            </Button>
          )}
          <Button
            onClick={() => {
              setIsOpen(true);
              setType("Subscription");
            }}
            type="primary"
          >
            Подписка
          </Button>
          <Button
            onClick={() => {
              setIsOpen(true);
              setType("Details");
            }}
            type="secondary"
          >
            Детали
          </Button>
        </div>
        <h3 className="slider-card__long-title">{card?.title}</h3>
      </motion.div>
      {isOpen && type ? (
        <DynamicModal
          isDarkerFull
          handleClose={() => {
            setIsOpen(false);
            document.body.classList.remove("no-scroll");
          }}
        >
          {type === "Details" ? (
            <MediaDetailsModal
              setIsModalOpen={(value) => {
                setIsOpen(value);
                setType(undefined);
              }}
              card={card}
            />
          ) : null}
          {type === "Video" ? (
            <MediaVideo
              video={card.resource?.url_absolute!}
              setIsModalOpen={(value) => {
                setIsOpen(value);
                setType(undefined);
              }}
            />
          ) : null}
          {type === "Subscription" ? (
            <SubscriptionHardCodedModal
              setIsModalOpen={(value) => {
                setIsOpen(value);
                setType(undefined);
              }}
            />
          ) : null}
        </DynamicModal>
      ) : null}
    </div>
  );
};

type MediaSubscriptionProps = {
  setIsModalOpen: (value: boolean) => void;
};

export const SubscriptionHardCodedModal = ({
  setIsModalOpen,
}: MediaSubscriptionProps) => {
  const t = useTranslations("common");
  const [price, setPrice] = useState<"rub" | "eur">("rub");
  const { data: plans } = useGetPlans({ type: PlanTypeValueType.Media });
  useEffect(() => {
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <div className="subscription-modal">
      <button
        onClick={() => {
          setIsModalOpen(false);
          document.body.classList.remove("no-scroll");
        }}
        className="course-card__modal-close"
      >
        <CloseIcon />
      </button>
      <div className="subscription-modal__holder">
        <h2 className="subscription-modal__title">{plans?.[0].title}</h2>
        <p className="subscription-modal__text">
          Эта подписка даст вам доступ ко всем разделам Медиа Библиотеки + новым
          Мини-Курсам Академии{" "}
        </p>
        <div
          className={`subscription-modal__switch ${
            price === "eur" ? "subscription-modal__switch--right" : ""
          }`.trim()}
        >
          <button
            className={`subscription-modal__switch-button ${
              price === "rub" ? "subscription-modal__switch-button--active" : ""
            }`.trim()}
            onClick={() => setPrice("rub")}
          >
            РУБ
          </button>
          <button
            className={`subscription-modal__switch-button ${
              price === "eur" ? "subscription-modal__switch-button--active" : ""
            }`.trim()}
            onClick={() => setPrice("eur")}
          >
            ЕВРО
          </button>
        </div>
        <div className="subscription-modal__prices">
          <PriceCard
            title="Месячная"
            price={plans?.[0].price_monthly_eur!}
            priceRub={plans?.[0].price_monthly_rub! || 0}
            oldPrice={70}
            oldPriceRub={7000}
            type={price}
            list={[]}
          />
          <PriceCard
            title="Годовая"
            price={plans?.[0].price_annual_eur!}
            oldPrice={840}
            priceRub={plans?.[0].price_annual_rub! || 0}
            oldPriceRub={86000}
            isRecommended
            type={price}
            list={[]}
          />
        </div>
        <Link
          target="_blank"
          className="subscription-modal__terms"
          href={t("terms.link")}
        >
          {t("terms.title")}
        </Link>
      </div>
    </div>
  );
};

type PriceCardProps = {
  title: string;
  price: number;
  priceRub: number;
  oldPrice?: number;
  oldPriceRub?: number;
  isRecommended?: boolean;
  type: "rub" | "eur";
  list: string[];
};

const PriceCard = ({
  title,
  price,
  priceRub,
  isRecommended,
  oldPrice,
  oldPriceRub,
  type,
  list,
}: PriceCardProps) => {
  return (
    <div
      className={`price-card ${
        isRecommended ? "price-card--recommended" : ""
      }`.trim()}
    >
      <div>
        <h3 className="price-card__title">{title}</h3>
        <p className="price-card__price-eur price-card__price-eur--old">
          {type === "eur" ? (
            <>{(oldPrice || 0).toFixed()}€</>
          ) : priceRub > 0 ? (
            <>{(oldPriceRub || 0).toFixed()} RUB</>
          ) : null}
        </p>
        <div className="price-card__prices">
          <p className="price-card__price-eur price-card__price-eur--larger">
            {type === "eur" ? (
              <>{(price || 0).toFixed()}€</>
            ) : priceRub > 0 ? (
              <>{(priceRub || 0).toFixed()} RUB</>
            ) : null}
          </p>
          {priceRub > 0 || type === "eur" ? null : (
            <p className="price-card__price-eur price-card__price-eur--larger">
              Скоро
            </p>
          )}
        </div>
        <ul className="price-card__list" style={{ marginTop: "20px" }}>
          {list.map((item) => (
            <li
              className={`price-card__item ${
                isRecommended ? "price-card__item--invert" : ""
              }`.trim()}
              key={item}
            >
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

type MediaVideoProps = {
  video: string;
  setIsModalOpen: (value: boolean) => void;
};

const MediaVideo = ({ video, setIsModalOpen }: MediaVideoProps) => {
  const t = useTranslations("common");
  useEffect(() => {
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <div className="course-card__modal">
      <div className="course-card__modal-content">
        <button
          onClick={() => {
            setIsModalOpen(false);
            document.body.classList.remove("no-scroll");
          }}
          className="course-card__modal-close"
        >
          <CloseIcon />
        </button>
        <video
          loop={false}
          autoPlay={true}
          controls={true}
          playsInline
          disablePictureInPicture
          className="course-card__modal-video"
        >
          <source src={video} />
          {t("videoSupportText")}
        </video>
      </div>
    </div>
  );
};

type MediaDetailsProps = {
  card: ProductType;
  setIsModalOpen: (value: boolean) => void;
};

export const MediaDetailsModal = ({
  card,
  setIsModalOpen,
}: MediaDetailsProps) => {
  const [duration, setDuration] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isFlipped, setIsFlipped] = useState(false);

  const isComingSoon = card.activation_date
    ? isProductComingSoon(card.activation_date)
    : false;
  const isNew = card.activation_date
    ? isProductNew(card.activation_date)
    : false;

  useEffect(() => {
    if (!card.resource?.url_absolute) return;

    const videoElement = document.createElement("video");
    videoElement.src = card.resource?.url_absolute;

    const handleMetadataLoaded = () => {
      setDuration(videoElement.duration); // Duration in seconds
    };

    const handleError = (event: Event) => {
      console.error("Video loading error:", event);
      setError(
        "Failed to load the video. Please check the video URL or format."
      );
    };

    videoElement.addEventListener("loadedmetadata", handleMetadataLoaded);
    videoElement.addEventListener("error", handleError);

    videoElement.load();

    return () => {
      videoElement.removeEventListener("loadedmetadata", handleMetadataLoaded);
      videoElement.removeEventListener("error", handleError);
      videoElement.remove();
    };
  }, [card]);
  useEffect(() => {
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <div
      className={`media-details-modal-flip ${
        isFlipped ? "media-details-modal-flip--active" : ""
      }`.trim()}
    >
      <div className="media-details-modal">
        <button
          onClick={() => {
            setIsModalOpen(false);
            document.body.classList.remove("no-scroll");
          }}
          className="course-card__modal-close"
        >
          <CloseIcon />
        </button>
        <div
          className="media-details-modal__top"
          style={{ backgroundImage: `url(${card?.images?.primary_image_url})` }}
        >
          <div className="media-details-modal__tag">
            {isComingSoon ? (
              <p className="slider-card__tag--soon">Скоро</p>
            ) : null}
            {isNew ? <p className="slider-card__tag--new">Новое</p> : null}
            {!card.free ? (
              <Image
                src="/assets/images/Lock.png"
                alt="Locked"
                width={24}
                height={24}
              />
            ) : null}
          </div>
          <div className="media-details-modal__top-content">
            <h5 className="media-details-modal__top-title">{card?.title}</h5>
          </div>
        </div>
        <div className="media-details-modal__bottom">
          <div className="media-details-modal__bottom-content">
            {/* <p >
              {card?.description}
            </p> */}
            {!!card?.description ? (
              <Button
                type="secondary"
                disabled={!card?.description}
                onClick={() => setIsFlipped(true)}
              >
                Прочитать подробнее
              </Button>
            ) : null}
            {duration && duration > 0 ? (
              <p className="media-details-modal__bottom-duration">
                {secondsToHMS(duration)}
              </p>
            ) : null}
          </div>
        </div>
      </div>
      <div className="media-details-modal__back">
        <button
          onClick={() => {
            setIsModalOpen(false);
            document.body.classList.remove("no-scroll");
          }}
          className="course-card__modal-close"
        >
          <CloseIcon />
        </button>
        <Button
          type="primary"
          onClick={() => {
            setIsFlipped(false);
          }}
        >
          <Image
            src="/assets/images/left-arrow-black.svg"
            alt="left arrow"
            width={20}
            height={20}
          />
          Назад
        </Button>
        <div
          className="media-details-modal__bottom-description"
          dangerouslySetInnerHTML={{ __html: card?.description || "" }}
        />
      </div>
    </div>
  );
};

type MediaDetailsNewsProps = {
  card: NewsType;
  setIsModalOpen: (value: boolean) => void;
};

export const MediaDetailsNewsModal = ({
  card,
  setIsModalOpen,
}: MediaDetailsNewsProps) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const isComingSoon = card.product?.activation_date
    ? isProductComingSoon(card.product?.activation_date)
    : false;
  const isNew = card.product?.activation_date
    ? isProductNew(card.product?.activation_date)
    : false;

  useEffect(() => {
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <div
      className={`media-details-modal-flip ${
        isFlipped ? "media-details-modal-flip--active" : ""
      }`.trim()}
    >
      <div className="media-details-modal">
        <button
          onClick={() => {
            setIsModalOpen(false);
            document.body.classList.remove("no-scroll");
          }}
          className="course-card__modal-close"
        >
          <CloseIcon />
        </button>
        <div
          className="media-details-modal__top"
          style={{ backgroundImage: `url(${card?.image})` }}
        >
          <div className="media-details-modal__tag">
            {isComingSoon ? (
              <p className="slider-card__tag--soon">Скоро</p>
            ) : null}
            {isNew ? <p className="slider-card__tag--new">Новое</p> : null}
            <Image
              src="/assets/images/Lock.png"
              alt="Locked"
              width={24}
              height={24}
            />
          </div>
          <div className="media-details-modal__top-content">
            <h5 className="media-details-modal__top-title">{card?.title}</h5>
          </div>
        </div>
        <div className="media-details-modal__bottom">
          <div className="media-details-modal__bottom-content">
            {!!card?.description ? (
              <Button
                type="secondary"
                disabled={!card?.description}
                onClick={() => setIsFlipped(true)}
              >
                Прочитать подробнее
              </Button>
            ) : null}
          </div>
        </div>
      </div>
      <div className="media-details-modal__back">
        <button
          onClick={() => {
            setIsModalOpen(false);
            document.body.classList.remove("no-scroll");
          }}
          className="course-card__modal-close"
        >
          <CloseIcon />
        </button>
        <Button
          type="primary"
          onClick={() => {
            setIsFlipped(false);
          }}
        >
          <Image
            src="/assets/images/left-arrow-black.svg"
            alt="left arrow"
            width={20}
            height={20}
          />
          Назад
        </Button>
        <div
          className="media-details-modal__bottom-description"
          dangerouslySetInnerHTML={{ __html: card?.description || "" }}
        />
      </div>
    </div>
  );
};

export default SliderCards;
